import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface ModelSelectorProps {
  serviceType: string;
  selectedOption: string;
  onSelectionChange: (selection: ModelSelection) => void;
}

interface ModelSelection {
  type?: string;
  capacity?: string;
  brand?: string;
  material?: string;
  additionalInfo?: string;
}

// Définition des marques de ballons d'eau chaude
const ballonBrands = [
  'Atlantic', 'Thermor', 'Ariston', 'Chaffoteaux', 'De Dietrich', 'Sauter'
];

// Capacités pour ballons verticaux
const verticalCapacities = [
  '15L', '30L', '50L', '75L', '100L', '150L', '200L', '300L'
];

// Capacités pour ballons horizontaux  
const horizontalCapacities = [
  '45L', '75L', '100L', '150L', '200L'
];

// Marques de robinetterie
const faucetBrands = [
  'Grohe', 'Hansgrohe', 'Jacob Delafon', 'Ideal Standard'
];

// Types de receveurs de douche
const showerTypes = [
  'Douche à l\'italienne', 'Receveur classique'
];

// Matériaux de receveurs
const showerMaterials = [
  'Résine', 'Grès', 'Acrylique'
];

const ModelSelector: React.FC<ModelSelectorProps> = ({ 
  serviceType, 
  selectedOption, 
  onSelectionChange 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selection, setSelection] = useState<ModelSelection>({});

  const handleSelectionUpdate = (key: keyof ModelSelection, value: string) => {
    const newSelection = { ...selection, [key]: value };
    setSelection(newSelection);
    onSelectionChange(newSelection);
  };

  // Affichage conditionnel selon le type de service et l'option sélectionnée
  const renderSelectionOptions = () => {
    if (serviceType === "Ballon d'eau") {
      if (selectedOption.includes("Vertical")) {
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capacité du ballon vertical
              </label>
              <select 
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                onChange={(e) => handleSelectionUpdate('capacity', e.target.value)}
                value={selection.capacity || ''}
              >
                <option value="">Sélectionnez une capacité</option>
                {verticalCapacities.map(capacity => (
                  <option key={capacity} value={capacity}>{capacity}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marque préférée
              </label>
              <select 
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                onChange={(e) => handleSelectionUpdate('brand', e.target.value)}
                value={selection.brand || ''}
              >
                <option value="">Sélectionnez une marque</option>
                {ballonBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        );
      }
      
      if (selectedOption.includes("Horizontal")) {
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Capacité du ballon horizontal
              </label>
              <select 
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                onChange={(e) => handleSelectionUpdate('capacity', e.target.value)}
                value={selection.capacity || ''}
              >
                <option value="">Sélectionnez une capacité</option>
                {horizontalCapacities.map(capacity => (
                  <option key={capacity} value={capacity}>{capacity}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marque préférée
              </label>
              <select 
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                onChange={(e) => handleSelectionUpdate('brand', e.target.value)}
                value={selection.brand || ''}
              >
                <option value="">Sélectionnez une marque</option>
                {ballonBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        );
      }
    }

    if ((serviceType === "Douche" || serviceType === "Baignoire" || serviceType === "Évier") && 
        selectedOption.includes("robinet")) {
      
      if (selectedOption.includes("marque")) {
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marque de robinetterie préférée
            </label>
            <select 
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              onChange={(e) => handleSelectionUpdate('brand', e.target.value)}
              value={selection.brand || ''}
            >
              <option value="">Sélectionnez une marque</option>
              {faucetBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
        );
      }
    }

    if (serviceType === "Douche" && selectedOption.includes("receveur")) {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de douche souhaité
            </label>
            <select 
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              onChange={(e) => handleSelectionUpdate('type', e.target.value)}
              value={selection.type || ''}
            >
              <option value="">Sélectionnez un type</option>
              {showerTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Matériau du receveur
            </label>
            <select 
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              onChange={(e) => handleSelectionUpdate('material', e.target.value)}
              value={selection.material || ''}
            >
              <option value="">Sélectionnez un matériau</option>
              {showerMaterials.map(material => (
                <option key={material} value={material}>{material}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Informations complémentaires
            </label>
            <textarea 
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              placeholder="Emplacement (sur 1, 2 ou 3 murs), contraintes techniques, hauteur disponible..."
              onChange={(e) => handleSelectionUpdate('additionalInfo', e.target.value)}
              value={selection.additionalInfo || ''}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const needsModelSelection = () => {
    return (serviceType === "Ballon d'eau" && 
           (selectedOption.includes("Vertical") || selectedOption.includes("Horizontal"))) ||
           ((serviceType === "Douche" || serviceType === "Baignoire" || serviceType === "Évier") && 
            selectedOption.includes("robinet")) ||
           (serviceType === "Douche" && selectedOption.includes("receveur"));
  };

  if (!needsModelSelection()) {
    return null;
  }

  return (
    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <h4 className="font-medium text-blue-900">
          ✅ Personnaliser votre choix
        </h4>
        {isExpanded ? (
          <FiChevronUp className="text-blue-600" />
        ) : (
          <FiChevronDown className="text-blue-600" />
        )}
      </button>
      
      {isExpanded && (
        <div className="mt-4">
          {renderSelectionOptions()}
          
          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 Ces informations m'aideront à vous proposer un devis plus précis et adapté à vos besoins.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelSelector; 